import React, { useState, useEffect } from 'react';
import { Briefcase, Mail, Phone, Trash2, Download, Calendar, Send, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const InternshipManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Interview slots state
  const [slot1Date, setSlot1Date] = useState('');
  const [slot1Time, setSlot1Time] = useState('');
  const [slot2Date, setSlot2Date] = useState('');
  const [slot2Time, setSlot2Time] = useState('');
  const [slot3Date, setSlot3Date] = useState('');
  const [slot3Time, setSlot3Time] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/internship-applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await axios.delete(`${API}/internship-applications/${appId}`);
      toast.success('Application deleted successfully!');
      fetchApplications();
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Failed to delete application');
    }
  };

  const openScheduleModal = (app) => {
    setSelectedApp(app);
    // Reset slots
    setSlot1Date('');
    setSlot1Time('');
    setSlot2Date('');
    setSlot2Time('');
    setSlot3Date('');
    setSlot3Time('');
    setShowScheduleModal(true);
  };

  const formatSlot = (date, time) => {
    if (!date || !time) return '';
    const dateObj = new Date(`${date}T${time}`);
    return dateObj.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSendInvite = async () => {
    // Validate all slots are filled
    if (!slot1Date || !slot1Time || !slot2Date || !slot2Time || !slot3Date || !slot3Time) {
      toast.error('Please fill in all 3 interview time slots');
      return;
    }

    const formattedSlots = {
      slot1: formatSlot(slot1Date, slot1Time),
      slot2: formatSlot(slot2Date, slot2Time),
      slot3: formatSlot(slot3Date, slot3Time)
    };

    try {
      setSendingInvite(true);
      await axios.post(`${API}/internship-applications/${selectedApp.id}/send-interview-invite`, formattedSlots);
      toast.success(`Interview invite sent to ${selectedApp.email}!`);
      setShowScheduleModal(false);
      fetchApplications();
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Failed to send interview invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const exportToCSV = () => {
    if (applications.length === 0) {
      toast.error('No applications to export');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Experience', 'Interests', 'Availability', 'Status', 'Interview Time', 'Submitted Date'];
    const rows = applications.map(app => [
      app.name,
      app.email,
      app.phone,
      app.experience,
      app.interests,
      app.availability,
      app.status || 'pending',
      app.selected_slot || 'Not scheduled',
      new Date(app.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `internship_applications_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Applications exported!');
  };

  const getStatusBadge = (app) => {
    const status = app.status || 'pending';
    const badges = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', icon: Clock, label: 'Pending Review' },
      interview_pending: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: Send, label: 'Invite Sent' },
      interview_scheduled: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: CheckCircle, label: 'Interview Scheduled' },
      interview_completed: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', icon: CheckCircle, label: 'Interview Complete' },
      accepted: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: CheckCircle, label: 'Accepted' },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: XCircle, label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full ${badge.bg} ${badge.text} border ${badge.border} text-xs font-medium`}>
        <Icon size={12} className="mr-1" />
        {badge.label}
      </div>
    );
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Internship Applications</h2>
          <p className="text-gray-400 text-sm">
            {applications.length} application{applications.length !== 1 ? 's' : ''} received
          </p>
        </div>

        <button
          onClick={exportToCSV}
          disabled={applications.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download size={16} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Applications List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Loading applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-12 text-center">
          <Briefcase className="text-purple-400 mx-auto mb-4" size={48} />
          <p className="text-gray-400">No internship applications yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div
              key={app.id}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 hover:border-purple-500/50 transition-all"
            >
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                {/* Application Details */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{app.name}</h3>
                        {getStatusBadge(app)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center space-x-2 text-gray-300">
                          <Mail size={14} className="text-purple-400" />
                          <a href={`mailto:${app.email}`} className="hover:text-white transition-colors">
                            {app.email}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-300">
                          <Phone size={14} className="text-purple-400" />
                          <a href={`tel:${app.phone}`} className="hover:text-white transition-colors">
                            {app.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Submitted</p>
                      <p className="text-sm text-gray-300">
                        {new Date(app.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Interview Info */}
                  {app.selected_slot && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-green-400">
                        <Calendar size={16} />
                        <span className="font-semibold">Interview Scheduled:</span>
                        <span className="text-white">{app.selected_slot}</span>
                      </div>
                    </div>
                  )}

                  {app.status === 'interview_pending' && !app.selected_slot && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-blue-400">
                        <Send size={16} />
                        <span>Interview invite sent - awaiting response</span>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-black/30 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Experience</p>
                      <p className="text-sm text-gray-200">{app.experience}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Interests</p>
                      <p className="text-sm text-gray-200">{app.interests}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Availability</p>
                      <p className="text-sm text-gray-200">{app.availability}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex lg:flex-col gap-2 justify-end">
                  {(!app.status || app.status === 'pending') && (
                    <button
                      onClick={() => openScheduleModal(app)}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                    >
                      <Calendar size={16} />
                      <span>Schedule Interview</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(app.id)}
                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                  >
                    <Trash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && selectedApp && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-purple-900/50 border border-purple-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">Schedule Interview</h3>
              <p className="text-gray-400 text-sm mb-6">
                Select 3 interview time slots for <span className="text-purple-400 font-semibold">{selectedApp.name}</span>
              </p>

              <div className="space-y-4">
                {/* Slot 1 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <label className="text-sm text-purple-400 font-semibold mb-2 block">Option 1</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="date"
                      min={today}
                      value={slot1Date}
                      onChange={(e) => setSlot1Date(e.target.value)}
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                    <input
                      type="time"
                      value={slot1Time}
                      onChange={(e) => setSlot1Time(e.target.value)}
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  {slot1Date && slot1Time && (
                    <p className="text-xs text-gray-400 mt-2">→ {formatSlot(slot1Date, slot1Time)}</p>
                  )}
                </div>

                {/* Slot 2 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <label className="text-sm text-purple-400 font-semibold mb-2 block">Option 2</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="date"
                      min={today}
                      value={slot2Date}
                      onChange={(e) => setSlot2Date(e.target.value)}
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                    <input
                      type="time"
                      value={slot2Time}
                      onChange={(e) => setSlot2Time(e.target.value)}
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  {slot2Date && slot2Time && (
                    <p className="text-xs text-gray-400 mt-2">→ {formatSlot(slot2Date, slot2Time)}</p>
                  )}
                </div>

                {/* Slot 3 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <label className="text-sm text-purple-400 font-semibold mb-2 block">Option 3</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="date"
                      min={today}
                      value={slot3Date}
                      onChange={(e) => setSlot3Date(e.target.value)}
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                    <input
                      type="time"
                      value={slot3Time}
                      onChange={(e) => setSlot3Time(e.target.value)}
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  {slot3Date && slot3Time && (
                    <p className="text-xs text-gray-400 mt-2">→ {formatSlot(slot3Date, slot3Time)}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={sendingInvite}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {sendingInvite ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Invite
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternshipManagement;
